import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { ArrowRight, Loader2, Lock, Mail, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { checkAdminAccess, recordLoginAttempt, login } from "@/lib/api";
import { setToken, isAuthenticated } from "@/lib/auth";
const adminLogo = { url: "/media/investenergy-admin-logo.png" };

export const Route = createFileRoute("/auth")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Autentificare administrare — InvesTenergy 2026" },
      { name: "description", content: "Acces securizat la panoul de administrare InvesTenergy 2026." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Autentificare administrare — InvesTenergy 2026" },
      { property: "og:description", content: "Acces securizat la panoul de administrare InvesTenergy 2026." },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated()) navigate({ to: "/admin", replace: true });
  }, [navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const access = await checkAdminAccess().catch(() => ({ blocked: false, ip: null }));
    if (access.blocked) {
      await recordLoginAttempt({
        email: email.trim(), success: false, reason: "IP blocat",
      }).catch(() => {});
      setError("Accesul de la această adresă IP este blocat.");
      setLoading(false);
      return;
    }

    let signInError: Error | null = null;
    try {
      const res = await login(email.trim(), password);
      setToken(res.token);
    } catch (e) {
      signInError = e instanceof Error ? e : new Error("Eroare la autentificare");
    }
    await recordLoginAttempt({
      email: email.trim(),
      success: !signInError,
      ...(signInError ? { reason: "Email sau parolă incorecte" } : {}),
    }).catch(() => {});
    if (signInError) {
      setError("Email sau parolă incorecte.");
      setLoading(false);
      return;
    }
    navigate({ to: "/admin", replace: true });
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#04101a] px-4 py-16">
      {/* aurora energetică */}
      <motion.div
        aria-hidden
        className="absolute -left-40 top-0 h-[560px] w-[560px] rounded-full bg-[#198f31]/35 blur-[150px]"
        animate={{ x: [0, 80, 0], y: [0, 50, 0], scale: [1, 1.15, 1] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute -right-32 bottom-0 h-[520px] w-[520px] rounded-full bg-[#00347e]/50 blur-[150px]"
        animate={{ x: [0, -60, 0], y: [0, -40, 0], scale: [1, 1.2, 1] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* grilă energetică */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,.25) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,.25) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(ellipse at center, black 10%, transparent 72%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 10%, transparent 72%)",
        }}
      />

      {/* particule */}
      {Array.from({ length: 14 }).map((_, i) => (
        <motion.span
          key={i}
          aria-hidden
          className="absolute h-1 w-1 rounded-full bg-emerald-300/70"
          style={{ left: `${(i * 7.3 + 5) % 100}%`, bottom: "-2%" }}
          animate={{ y: [0, -700], opacity: [0, 1, 0] }}
          transition={{
            duration: 10 + (i % 5) * 3,
            repeat: Infinity,
            delay: i * 0.9,
            ease: "easeOut",
          }}
        />
      ))}

      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-white/[0.05] p-8 shadow-[0_30px_120px_-40px_rgba(25,143,49,0.7)] backdrop-blur-2xl"
      >
        <motion.span
          aria-hidden
          className="absolute inset-x-0 top-0 h-px"
          style={{ background: "linear-gradient(90deg,#198f31,#4ade80,#38bdf8)" }}
          animate={{ opacity: [0.35, 1, 0.35] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="flex flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15, duration: 0.5 }}
            className="relative"
          >
            <motion.span
              aria-hidden
              className="absolute inset-0 -z-10 rounded-full bg-emerald-400/30 blur-2xl"
              animate={{ scale: [1, 1.35, 1], opacity: [0.4, 0.8, 0.4] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
            <img src={adminLogo.url} alt="InvesTenergy" className="h-12 w-auto brightness-0 invert" />
          </motion.div>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-emerald-200">
            <ShieldCheck className="h-3.5 w-3.5" />
            Platformă de management
          </div>
          <h1 className="mt-4 text-2xl font-bold text-white">Bine ai revenit</h1>
          <p className="mt-2 text-sm leading-relaxed text-white/60">
            Autentifică-te pentru a gestiona conținutul.
          </p>
        </div>

        <form onSubmit={onSubmit} className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-white/80">
              Email
            </Label>
            <div className="group relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40 transition-colors group-focus-within:text-emerald-300" />
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 border-white/15 bg-white/5 pl-9 text-white transition-shadow placeholder:text-white/30 focus-visible:ring-emerald-400/40"
                placeholder="nume@exemplu.ro"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-white/80">
              Parolă
            </Label>
            <div className="group relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40 transition-colors group-focus-within:text-emerald-300" />
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 border-white/15 bg-white/5 pl-9 text-white transition-shadow placeholder:text-white/30 focus-visible:ring-emerald-400/40"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <motion.p
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: [0, -6, 6, -3, 0] }}
              className="text-sm text-red-400"
            >
              {error}
            </motion.p>
          )}

          <motion.div whileHover={{ scale: 1.015 }} whileTap={{ scale: 0.985 }} className="relative">
            <motion.span
              aria-hidden
              className="absolute -inset-1 rounded-2xl opacity-60 blur-xl"
              style={{ background: "linear-gradient(100deg,#198f31,#38bdf8,#00347e)" }}
              animate={{ opacity: [0.35, 0.75, 0.35] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
            />
            <Button
              type="submit"
              disabled={loading}
              className="group relative h-12 w-full overflow-hidden rounded-xl border-0 text-sm font-semibold tracking-wide text-white shadow-lg"
            >
              <motion.span
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(100deg,#198f31,#0f6fa8,#00347e,#0f6fa8,#198f31)",
                  backgroundSize: "300% 100%",
                }}
                animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              />
              <span className="absolute inset-y-0 -left-1/3 w-1/3 skew-x-[-18deg] bg-white/25 blur-md transition-all duration-700 group-hover:left-[110%]" />
              <span className="relative flex items-center justify-center gap-2">
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Se conectează…
                  </>
                ) : (
                  <>
                    Intră în cont
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </span>
            </Button>
          </motion.div>

          <p className="text-center text-xs text-white/40">
            Conexiune criptată. Accesul este monitorizat și auditat.
          </p>
        </form>
      </motion.div>
    </div>
  );
}

